import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mountain, ArrowLeft, Upload, Plus, X, MapPin, IndianRupee, Calendar, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const eventTypes = ['Festival', 'Music', 'Literature', 'Rally', 'Biking', 'Cultural', 'Adventure', 'Wellness', 'Other'];

const CreateEventListing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: '',
    start_date: '',
    end_date: '',
    venue: { name: '', city: '', state: '', country: 'India' },
    schedule: [],
    ticket_types: [],
    images: [],
    organizer_info: '',
    terms_conditions: ''
  });
  const [imageUrls, setImageUrls] = useState(['']);
  const [newTicket, setNewTicket] = useState({ name: '', price: '', quantity: '', description: '' });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('supplier_token');
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('supplier_token');
    if (!token) navigate('/supplier/onboarding');
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addTicketType = () => {
    if (newTicket.name && newTicket.price) {
      setFormData(prev => ({
        ...prev,
        ticket_types: [...prev.ticket_types, {
          name: newTicket.name,
          price: parseFloat(newTicket.price),
          quantity: parseInt(newTicket.quantity) || 100,
          description: newTicket.description
        }]
      }));
      setNewTicket({ name: '', price: '', quantity: '', description: '' });
    }
  };

  const removeTicket = (index) => {
    setFormData(prev => ({ ...prev, ticket_types: prev.ticket_types.filter((_, i) => i !== index) }));
  };

  const addImageUrl = () => setImageUrls(prev => [...prev, '']);
  const updateImageUrl = (index, value) => {
    setImageUrls(prev => { const updated = [...prev]; updated[index] = value; return updated; });
  };
  const removeImageUrl = (index) => setImageUrls(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please enter event dates');
      return;
    }
    if (!formData.venue.city || !formData.venue.state) {
      toast.error('Please enter venue details');
      return;
    }

    const validImages = imageUrls.filter(url => url.trim() !== '');
    if (validImages.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        images: validImages,
        videos: [],
        schedule: formData.schedule.length > 0 ? formData.schedule : [{ time: '10:00', activity: 'Event begins' }],
        ticket_types: formData.ticket_types.length > 0 ? formData.ticket_types : [{ name: 'General', price: 0, quantity: 100, description: 'General admission' }]
      };

      const response = await fetch(`${API_URL}/api/supplier/listings/events`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Event listing created successfully!');
        navigate('/supplier/dashboard');
      } else {
        toast.error(data.detail || 'Failed to create listing');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/supplier/dashboard')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" /> Back to Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Mountain className="h-6 w-6 text-purple-600" />
              <span className="font-bold text-purple-600">GoHimalayan</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Playfair Display, serif'}}>
          Add Your Event
        </h1>
        <p className="text-gray-600 mb-8">List your festival or event on GoHimalayan</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                <Input name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., Kasauli Literature Festival 2026" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your event, what attendees can expect..."
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
                <div className="flex flex-wrap gap-2">
                  {eventTypes.map(type => (
                    <button key={type} type="button" onClick={() => setFormData(prev => ({ ...prev, event_type: type.toLowerCase() }))}
                      className={`px-3 py-1.5 rounded-full text-sm ${formData.event_type === type.toLowerCase() ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Start Date *
                  </label>
                  <Input type="date" name="start_date" value={formData.start_date} onChange={handleInputChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> End Date *
                  </label>
                  <Input type="date" name="end_date" value={formData.end_date} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </div>

          {/* Venue */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><MapPin className="w-5 h-5" /> Venue</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name</label>
                <Input name="venue.name" value={formData.venue.name} onChange={handleInputChange} placeholder="e.g., Heritage Resort & Spa" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <Input name="venue.city" value={formData.venue.city} onChange={handleInputChange} placeholder="e.g., Kasauli" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <Input name="venue.state" value={formData.venue.state} onChange={handleInputChange} placeholder="e.g., Himachal Pradesh" />
              </div>
            </div>
          </div>

          {/* Ticket Types */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Ticket className="w-5 h-5" /> Ticket Types</h2>
            
            <div className="grid sm:grid-cols-4 gap-3 mb-4">
              <Input placeholder="Ticket name" value={newTicket.name} onChange={(e) => setNewTicket(prev => ({ ...prev, name: e.target.value }))} />
              <Input type="number" placeholder="Price (₹)" value={newTicket.price} onChange={(e) => setNewTicket(prev => ({ ...prev, price: e.target.value }))} />
              <Input type="number" placeholder="Quantity" value={newTicket.quantity} onChange={(e) => setNewTicket(prev => ({ ...prev, quantity: e.target.value }))} />
              <Button type="button" onClick={addTicketType} variant="outline">Add Ticket</Button>
            </div>

            {formData.ticket_types.length > 0 && (
              <div className="space-y-2">
                {formData.ticket_types.map((ticket, i) => (
                  <div key={i} className="flex items-center justify-between bg-purple-50 p-3 rounded-lg">
                    <div>
                      <span className="font-medium">{ticket.name}</span>
                      <span className="text-purple-600 ml-2">₹{ticket.price}</span>
                      <span className="text-gray-500 ml-2">({ticket.quantity} available)</span>
                    </div>
                    <button type="button" onClick={() => removeTicket(i)} className="text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            
            {formData.ticket_types.length === 0 && (
              <p className="text-sm text-gray-500">No tickets added. A free "General Admission" will be created by default.</p>
            )}
          </div>

          {/* Images */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2"><Upload className="w-5 h-5" /> Event Images</h2>
            <div className="space-y-3">
              {imageUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input value={url} onChange={(e) => updateImageUrl(index, e.target.value)} placeholder="https://example.com/image.jpg" className="flex-1" />
                  {imageUrls.length > 1 && <button type="button" onClick={() => removeImageUrl(index)} className="p-2 text-red-500"><X className="w-5 h-5" /></button>}
                </div>
              ))}
              <button type="button" onClick={addImageUrl} className="flex items-center gap-2 text-purple-600 hover:underline text-sm"><Plus className="w-4 h-4" /> Add another image</button>
            </div>
          </div>

          {/* Organizer Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organizer Info</label>
                <textarea
                  name="organizer_info"
                  value={formData.organizer_info}
                  onChange={handleInputChange}
                  placeholder="Brief info about the organizers..."
                  className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                <textarea
                  name="terms_conditions"
                  value={formData.terms_conditions}
                  onChange={handleInputChange}
                  placeholder="Any terms and conditions for attendees..."
                  className="w-full h-20 px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/supplier/dashboard')} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-purple-600 hover:bg-purple-700">{loading ? 'Creating...' : 'Create Event Listing'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventListing;
